import React, {useCallback, useEffect, useState} from "react";
import {AdNetworkCallbackLog, AdNetworkCallbackStatus, AdNetworkEventType} from "../../http/protocol";
import {AdNetworkService} from "../../services/AdNetworkService";
import {t} from "../../i18n";
import {buildLogRequest} from "./adNetworkUtils";

interface Props {
    service: AdNetworkService;
    /** Bumped by the parent to force a reload (e.g. after a test callback). */
    refreshToken: number;
}

const PAGE_SIZE = 25;

const statusBadge = (status?: AdNetworkCallbackStatus) => {
    const className = status === AdNetworkCallbackStatus.DELIVERED ? "badge badge-success"
        : status === AdNetworkCallbackStatus.FAILED ? "badge badge-danger"
        : "badge badge-warning";
    return <span className={className} data-testid="ad-network-log-status">{status}</span>;
};

export const CallbackLogTable = ({service, refreshToken}: Props) => {
    const [items, setItems] = useState<AdNetworkCallbackLog[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [userId, setUserId] = useState("");
    const [eventType, setEventType] = useState<AdNetworkEventType | "">("");
    const [status, setStatus] = useState<AdNetworkCallbackStatus | "">("");
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(() => {
        service.listLogs(buildLogRequest({page, size: PAGE_SIZE, userId, eventType, status}))
            .then(response => {
                setItems(response.items ?? []);
                setTotal(response.total ?? 0);
                setError(undefined);
            })
            .catch(() => setError(t("ad-networks.log.load-error", undefined, "Failed to load callback log")));
    }, [service, page, userId, eventType, status]);

    useEffect(() => {
        load();
    }, [load, refreshToken]);

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="card-box" data-testid="ad-network-log">
            <h4>{t("ad-networks.log.title", undefined, "Delivery log")}</h4>
            <div className="row">
                <div className="col-md-3 form-group">
                    <label htmlFor="ad-network-log-user">{t("ad-networks.log.user-id", undefined, "User ID")}</label>
                    <input id="ad-network-log-user" data-testid="ad-network-log-user-filter" className="form-control" value={userId}
                           onChange={e => { setUserId(e.target.value); setPage(0); }}/>
                </div>
                <div className="col-md-3 form-group">
                    <label htmlFor="ad-network-log-event">{t("ad-networks.log.event", undefined, "Event")}</label>
                    <select id="ad-network-log-event" data-testid="ad-network-log-event-filter" className="form-control" value={eventType}
                            onChange={e => { setEventType(e.target.value as AdNetworkEventType | ""); setPage(0); }}>
                        <option value="">{t("ad-networks.filter.all", undefined, "All")}</option>
                        <option value={AdNetworkEventType.SIGNUP}>{t("ad-networks.event.signup", undefined, "Signup")}</option>
                        <option value={AdNetworkEventType.FTD}>{t("ad-networks.event.ftd", undefined, "FTD")}</option>
                    </select>
                </div>
                <div className="col-md-3 form-group">
                    <label htmlFor="ad-network-log-status-filter">{t("ad-networks.log.status", undefined, "Status")}</label>
                    <select id="ad-network-log-status-filter" data-testid="ad-network-log-status-filter" className="form-control" value={status}
                            onChange={e => { setStatus(e.target.value as AdNetworkCallbackStatus | ""); setPage(0); }}>
                        <option value="">{t("ad-networks.filter.all", undefined, "All")}</option>
                        <option value={AdNetworkCallbackStatus.DELIVERED}>{t("ad-networks.status.delivered", undefined, "Delivered")}</option>
                        <option value={AdNetworkCallbackStatus.FAILED}>{t("ad-networks.status.failed", undefined, "Failed")}</option>
                        <option value={AdNetworkCallbackStatus.SKIPPED}>{t("ad-networks.status.skipped", undefined, "Skipped")}</option>
                    </select>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="table-responsive">
                <table className="table table-striped table-sm">
                    <thead>
                    <tr>
                        <th>{t("ad-networks.log.col-time", undefined, "Time")}</th>
                        <th>{t("ad-networks.log.col-destination", undefined, "Destination")}</th>
                        <th>{t("ad-networks.log.col-user", undefined, "User")}</th>
                        <th>{t("ad-networks.log.col-event", undefined, "Event")}</th>
                        <th>{t("ad-networks.log.col-status", undefined, "Status")}</th>
                        <th>{t("ad-networks.log.col-details", undefined, "Details")}</th>
                        <th>{t("ad-networks.log.col-amount-eur", undefined, "Amount (EUR)")}</th>
                        <th>{t("ad-networks.log.col-url", undefined, "URL")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {items.map(item => (
                        <tr key={item.id} data-testid="ad-network-log-row">
                            <td>{item.created ? new Date(item.created).toLocaleString() : ""}</td>
                            <td>{item.destinationName ?? item.destinationId}{item.test ? " " + t("ad-networks.log.test-suffix", undefined, "(test)") : ""}</td>
                            <td>{item.userId}</td>
                            <td>{item.eventType}</td>
                            <td>{statusBadge(item.status)}</td>
                            <td>
                                {item.status === AdNetworkCallbackStatus.SKIPPED
                                    ? item.skipReason
                                    : <span title={item.responseBody}>{item.responseCode ?? t("ad-networks.log.no-response", undefined, "no response")}</span>}
                            </td>
                            <td>{item.amountEuro != null ? item.amountEuro.toFixed(2) : ""}</td>
                            <td className="text-truncate" style={{maxWidth: "260px"}} title={item.finalUrl}>
                                {item.finalUrl}
                            </td>
                        </tr>
                    ))}
                    {items.length === 0 && (
                        <tr>
                            <td colSpan={8} className="text-muted">{t("ad-networks.log.empty", undefined, "No callback log entries")}</td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex align-items-center">
                <button className="btn btn-sm btn-secondary mr-2" data-testid="ad-network-log-prev" disabled={page <= 0}
                        onClick={() => setPage(page - 1)}>{t("ad-networks.pager.previous", undefined, "Previous")}</button>
                <span className="mr-2">
                    {t("ad-networks.pager.page", undefined, "Page")} {page + 1} / {pages} — {total} {t("ad-networks.pager.entries", undefined, "entries")}
                </span>
                <button className="btn btn-sm btn-secondary" data-testid="ad-network-log-next" disabled={page >= pages - 1}
                        onClick={() => setPage(page + 1)}>{t("ad-networks.pager.next", undefined, "Next")}</button>
            </div>
        </div>
    );
};
